<template>
  <div class="content-title-images" :style="{'background-color': survey.colors.splashBg}" :key="render" v-if="question.showImageOnTitle">
    <div class="title-images-container">
      <div class="title-images-content">
        <draggable
          v-model="question.imageOnTitle"
          :animation="200"
          :disabled="false"
          ghost-class="ghost-card"
          drag-class="dragging"
          :move="checkMove"
          group="title-images"
          @change="optionOrderChanged"
          @start="onDragStart"
          @end="onDragEnd"
          class="draggable-container">
          <template>
            <transition-group name="image-list" tag="div" class="image-list-container">
              <div v-for="(option, optionIndex) in question.imageOnTitle"
                class="title-image-choice"
                :class="{ 'is-dragging': isDragging }"
                :key="`title-image-${optionIndex}`"
                :data-size="currentTitleSize">
                <div class="image-container">
                  <div class="image-wrapper">
                    <ImageWrapper
                      :uploaderId="`uploader-title-${optionIndex}`"
                      :uploaderTitle="$t('sendhub.set_image_text')"
                      :imageProps="imagePropsDefault(question, optionIndex, option)"
                      :canDelete="canRemoveImage()"
                      :deleteTitle="$t('survey.delete_image')"
                      :disableMenus="isDragging"
                      :menuOptions="[]"
                      @delete="removeImage(optionIndex)"
                      @resetAutoStartUpload="option.autoStartUpload = false"
                      @submenu-action="handleImageAction($event, optionIndex)"
                    />
                  </div>
                </div>
                <div class="option-value" v-show="!question.hideTitleLabels">
                  <div class="input-wrapper">
                    <textarea
                      :value="option.value"
                      class="option-value-input"
                      :class="{
                        'has-error': option.error,
                        'is-focused': focusedIndex === optionIndex
                      }"
                      :placeholder="$t('survey.option_value_placeholder')"
                      :data-index="optionIndex"
                      maxlength="300"

                      @input="handleOptionInput($event, optionIndex)"
                      @focus="handleFocus($event, optionIndex)"
                      @blur="handleBlur($event, optionIndex)"
                      @click="handleClick($event, optionIndex)"
                      rows="1"
                      required
                    ></textarea>
                    <div class="character-counter">
                      <span>{{ option.value ? option.value.length : 0 }}/300</span>
                    </div>
                  </div>
                  <div v-if="option.error" class="option-error">{{ option.error }}</div>
                </div>
              </div>
              <div class="title-image-choice add-image-button no-drag"
                   :data-size="currentTitleSize"
                   @click="addOption"
                   v-title="canAddImage ? $t('survey.add_new_option') : $t('survey.max_title_images')"
                   :class="{ 'disabled': !canAddImage }"
                   :key="'add-button'">
                <div class="image-wrapper add-wrapper">
                  <div class="add-content">
                    <i class="material-symbols-outlined">add_circle</i>
                    <span>{{ $t('survey.add_image') }}</span>
                  </div>
                </div>
              </div>
            </transition-group>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from '@/_helpers/eventBus.js'
import draggable from 'vuedraggable'
import ImageWrapper from '@/components/survey/ImageWrapper.vue'

export default {
  name: 'TitleImages',
  props: ['question'],
  components: {
    draggable,
    ImageWrapper
  },
  data () {
    return {
      render: 0,
      isDragging: false,
      focusedIndex: null
    }
  },
  computed: {
    survey () {
      return this.$store.getters['survey/getSurvey']
    },
    currentTitleSize: {
      get () {
        return this.question.titleImageSize || 'medium'
      },
      set (value) {
        this.$set(this.question, 'titleImageSize', value)
      }
    },
    canAddImage () {
      return !this.question.imageOnTitle || this.question.imageOnTitle.length < 10
    }
  },
  methods: {
    onDragStart () {
      this.isDragging = true
      eventBus.$emit('dragging-started')
    },
    onDragEnd () {
      this.isDragging = false
      eventBus.$emit('dragging-ended')
      this.$nextTick(() => {
        this.render += 1
      })
    },
    imagePropsDefault (question, imageIndex, item) {
      return {
        id: question.id,
        success: (item && item.label),
        where: 'imageOnTitle',
        autoStartUpload: item?.autoStartUpload,
        label: item?.label,
        imageIndex
      }
    },
    handleImageAction (action, imageIndex) {
      if (action === 'delete-image') {
        this.removeImage(imageIndex)
      } else if (action === 'toggle-visibility') {
        const option = this.question.imageOnTitle[imageIndex]
        this.$set(option, 'isHidden', !option.isHidden)
        this.$store.commit('survey/upsertQuestion', { ...this.question })
      }
    },
    canRemoveImage () {
      return this.question.imageOnTitle && this.question.imageOnTitle.length > 1
    },
    removeImage (imageIndex) {
      if (!this.canRemoveImage()) return

      this.question.imageOnTitle.splice(imageIndex, 1)
      this.$store.commit('survey/upsertQuestion', this.question)
      this.render += 1
    },
    addOption () {
      if (!this.question.imageOnTitle) {
        this.question.imageOnTitle = []
      }

      // Check if we've reached the limit of 10 images
      if (this.question.imageOnTitle.length >= 10) {
        this.$toast.error('Maximum of 10 title images allowed')
        return
      }

      const newImage = {
        value: `imagem ${this.question.imageOnTitle.length + 1}`,
        label: 'https://img.binds.co/437e8dd8-82df-4a39-a30c-43ee8f8a6eea/-/crop/255x237/18,18/-/preview/',
        autoStartUpload: true
      }

      this.question.imageOnTitle.push(newImage)
      this.$store.commit('survey/upsertQuestion', this.question)
      this.render += 1

      // Scroll to the add button after the DOM has updated
      this.$nextTick(() => {
        const addButton = document.querySelector('.title-images-container .add-image-button')
        if (addButton) {
          const buttonRect = addButton.getBoundingClientRect()
          const offset = window.pageYOffset + buttonRect.top - (window.innerHeight / 2) + (buttonRect.height / 2)
          window.scrollTo({
            top: Math.max(0, offset),
            behavior: 'smooth'
          })
        }
      })
    },
    optionOrderChanged () {
      this.$store.commit('survey/upsertQuestion', this.question)
      this.render += 1
    },
    handleOptionInput (event, optionIndex) {
      const textarea = event.target
      let value = textarea.value

      // Store current scroll position and selection
      const scrollTop = textarea.scrollTop
      const selectionStart = textarea.selectionStart
      const selectionEnd = textarea.selectionEnd

      // Clean the input value
      value = value
        .replace(/\r?\n|\r/g, '') // Remove newlines
        .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
        .replace(/,/g, '') // Remove commas
        .slice(0, 300) // Limit length

      // Update the value first
      this.$set(this.question.imageOnTitle[optionIndex], 'value', value)

      // Then adjust height if focused
      if (this.focusedIndex === optionIndex) {
        this.$nextTick(() => {
          // Set textarea value directly to ensure proper height calculation
          textarea.value = value

          // Reset height to get proper scrollHeight
          textarea.style.height = '32px'
          const scrollHeight = Math.min(textarea.scrollHeight, 100)
          textarea.style.height = `${scrollHeight}px`

          // Restore scroll position and selection
          textarea.scrollTop = scrollTop
          textarea.setSelectionRange(selectionStart, selectionEnd)
        })
      }

      // Update error state
      if (!value.trim()) {
        this.$set(this.question.imageOnTitle[optionIndex], 'error', this.$t('survey.required_field'))
      } else {
        this.$set(this.question.imageOnTitle[optionIndex], 'error', null)
      }
    },
    checkMove (e) {
      // Prevent dragging if the element has the no-drag class
      return !e.draggedContext.element.value?.includes('add-button')
    },
    handleClick (event, index) {
      event.preventDefault()
      this.handleFocus(event, index)
    },
    handleFocus (event, index) {
      const textarea = event.target

      // Set focused index
      this.focusedIndex = index
      requestAnimationFrame(() => {
        // Calculate proper height
        textarea.style.height = '32px'
        const scrollHeight = Math.min(textarea.scrollHeight, 100)
        textarea.style.height = `${scrollHeight}px`
      })
    },
    handleBlur (event, index) {
      const textarea = event.target
      const relatedTarget = event.relatedTarget

      // Only collapse if we're not focusing another textarea
      if (!relatedTarget || !relatedTarget.classList.contains('option-value-input')) {
        this.focusedIndex = null

        // Collapse height with transition
        textarea.style.height = '32px'

        // Check for empty value
        const value = this.question.imageOnTitle[index].value
        if (!value?.trim()) {
          this.$set(this.question.imageOnTitle[index], 'error', this.$t('survey.required_field'))
        }

        this.$store.commit('survey/upsertQuestion', { ...this.question })
      }
    }
  },
  beforeDestroy () {
    // Remove the debounceTimer cleanup since we're not using it anymore
  },
  mounted () {
    // Initialize heights for all textareas
    this.$nextTick(() => {
      document.querySelectorAll('.option-value-input').forEach(textarea => {
        textarea.style.height = 'auto'
        const minHeight = 32
        const contentHeight = Math.max(textarea.scrollHeight, minHeight)
        textarea.style.height = `${contentHeight}px`
      })
    })
  }
}
</script>

<style lang="scss">
.content-title-images {
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.2s ease;
  &:hover {
    border-color: var(--accent-color, #ff607d);
  }

  .title-images-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .title-images-content {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    width: 100%;
    min-height: 150px;
    padding: 15px;
    box-sizing: border-box;
  }

  .image-list-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    width: 100%;
  }

  .title-image-choice {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    cursor: move;
    cursor: grab;
    margin-bottom: 15px;
    transition: all 0.3s ease;

    // Dynamic sizes based on selectedTitleSize
    &[data-size="small"] {
      width: calc(25% - 15px);
      min-width: 104px;

      .option-wrapper {
        width: 100%;
        height: 110px;
      }
    }

    &[data-size="medium"] {
      width: calc(33.33% - 15px);
      min-width: 150px;

      .image-wrapper {
        width: 160px;
        height: 160px;
      }
    }

    &[data-size="large"] {
      width: calc(38% - 15px);
      min-width: 400px;

      .image-wrapper {
        width: 200px;
        height: 200px;
      }
    }

    @media (max-width: 768px) {
      &[data-size="small"] {
        width: calc(33.33% - 15px);

        .image-wrapper {
          width: 140px;
          height: 140px;
        }
      }

      &[data-size="medium"] {
        width: calc(50% - 15px);

        .image-wrapper {
          width: 180px;
          height: 180px;
        }
      }

      &[data-size="large"] {
        width: 100%;

        .image-wrapper {
          width: 220px;
          height: 220px;
        }
      }
    }

    @media (max-width: 480px) {
      width: 100% !important;

      .image-wrapper {
        width: 180px !important;
        height: 180px !important;
      }
    }

    &:active {
      cursor: grabbing;
    }

    &.is-dragging {
      .menu-container {
        display: none !important;
      }
    }

    .image-container {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
    }

    .delete-button {
      position: absolute;
      top: -8px;
      right: -8px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: white;
      border: none;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      opacity: 0;
      transform: scale(0.8);

      i {
        font-size: 18px;
        color: var(--accent-color, #ff607d);
      }

      &:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      }
    }

    &:hover .delete-button {
      opacity: 1;
      transform: scale(1);
    }

    .image-wrapper {
      position: relative;
      margin-bottom: 0;
      background-color: #f8f8f8;
      border-radius: 4px;
      overflow: visible;
      transition: none;

      :deep(.image-uploader-container),
      :deep(.content-upload-setImage),
      :deep(.uploader-content),
      :deep(.upload-wrapper) {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }

    .option-value {
      margin-top: 8px;
      max-width: 200px;
      text-align: center;
      padding: 0px;
      box-sizing: border-box;

      .input-wrapper {
        position: relative;
        width: 100%;

        &::after {
          display: none;
        }
      }

      textarea.option-value-input {
        width: 100%;
        height: 32px;
        min-height: 32px;
        margin: 0;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 13px;
        color: #333;
        text-align: center;
        background-color: #fff;
        box-sizing: border-box;
        resize: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 1.4;
        will-change: transform, height;
        transform-origin: top;

        // Improved transition properties
        transition:
          height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          border-color 0.2s ease,
          background-color 0.2s ease;

        &.is-focused {
          max-height: 100px;
          text-align: left;
          white-space: pre-wrap;
          overflow-y: auto;
          background-color: #fff;
          border-color: var(--accent-color, #ff607d);
          box-shadow: 0 0 0 2px rgba(255, 96, 125, 0.1);
        }

        &:not(.is-focused) {
          background-color: #f8f8f8;
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          user-select: none;
          cursor: pointer;
        }

        &.has-error {
          border-color: var(--accent-color, #ff607d);
          background-color: #fff9f9;

          &::placeholder {
            color: var(--accent-color, #ff607d);
          }
        }
      }

      .character-counter {
        position: absolute;
        right: 0;
        bottom: -17px;
        font-size: 11px;
        color: #666;
        text-align: right;
        background: white;
        padding: 2px 4px;
        border-radius: 2px;
      }
    }

    .option-error {
      color: var(--accent-color, #ff607d);
      font-size: 11px;
      margin-top: 4px;
      text-align: left;
    }
  }

  .add-image-button {
    cursor: pointer !important;

    &.disabled {
      cursor: not-allowed !important;
      opacity: 0.5;

      .add-wrapper {
        border-color: #ddd !important;
        background: #f5f5f5 !important;

        .add-content {
          color: #999 !important;
        }
      }

      &:hover .add-wrapper {
        border-color: #ddd !important;
        background: #f5f5f5 !important;

        .add-content {
          color: #999 !important;
        }
      }
    }

    .add-wrapper {
      border: 2px dashed #ddd;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      background: white;

      .add-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: #666;

        i {
          font-size: 32px;
        }

        span {
          font-size: 14px;
        }
      }
    }

    &:hover .add-wrapper {
      border-color: var(--accent-color, #ff607d);
      background: #fff;

      .add-content {
        color: var(--accent-color, #ff607d);
      }
    }
  }

  .ghost-card {
    opacity: 0.5;
    background: #f0f0f0;
    border: 1px dashed #999;

    .menu-container {
      display: none !important;
    }
  }

  .dragging {
    opacity: 0.8;
    transform: rotate(2deg);

    .menu-container {
      display: none !important;
    }
  }

  // Add transition animations
  .image-list-enter-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .image-list-leave-active {
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    position: absolute;
  }

  .image-list-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  .image-list-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .image-list-move {
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .no-drag {
    cursor: pointer !important;
    user-select: none !important;
    -webkit-user-drag: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
  }

  .draggable-container .no-drag {
    pointer-events: auto !important;
  }
}

// Add fade-in animation for scrollbar
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Add keyframes for height transitions
@keyframes expandHeight {
  from {
    transform: scaleY(0.8);
    opacity: 0.8;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes collapseHeight {
  from {
    transform: scaleY(1);
    opacity: 1;
  }
  to {
    transform: scaleY(0.8);
    opacity: 0.8;
  }
}
</style>
